<template>
  <div>
    <modal :name="proposalName" :width="400" height="auto" scrollable adaptive :click-to-close="false" class="modal-not-overflow" @before-close="beforeClose">
      <div class="popup">
        <button class="popup__close" type="button" aria-label="Close" :disabled="busy" @click="$modal.hide(proposalName)" />
        <div class="popup__title">Propose Tours Funding</div>
        <div class="popup__body">
          <form @submit.prevent="propose">
            <app-form-group label="Selection budget" :label-for="proposalName + '-target'" required>
              <app-input :id="proposalName + '-target'" v-model="selectionBudget" inputmode="decimal" :disabled="busy" :error="budgetInvalid" />
              <template v-if="budgetInvalid" #error>Enter a positive amount with at most 2 decimal places</template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="proposeButton" type="submit" aria-label="Propose" :aria-busy="busy" :disabled="busy">Propose</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal :name="modalName" :width="1000" height="auto" scrollable adaptive :click-to-close="false" class="modal-not-overflow" @before-close="beforeClose" @closed="onClosed">
      <div class="popup">
        <button class="popup__close" type="button" aria-label="Close" :disabled="busy" @click="close" />
        <div class="popup__title">{{ batch ? 'Edit draft' : 'Request funding' }}</div>
        <div class="popup__body">
          <form @submit.prevent="createBatch">
            <app-cells v-if="direction">{{ relationshipName(direction) }} · USD</app-cells>
            <app-cells v-if="historical">Historical payment: you can reduce amounts or remove tours. Adding tours, increasing amounts and confirmation require reconciliation.</app-cells>
            <app-cells v-else-if="batch && partial">Saved calculations remain unchanged. Increases are checked against current eligibility and payer coverage when you save.</app-cells>
            <div v-for="row in rows" :key="groupId(row.group)" class="funding-editor__line">
              <div>
                <strong>{{ groupName(row.group) }}</strong>
                <div class="funding-editor__hint">First activity: {{ formatDate(row.first_activity) }}</div>
                <div v-if="partial" class="funding-editor__hint">
                  {{ row.retained ? 'Saved requirement' : 'Funding requirement' }}: {{ formatAmount(row.retained ? row.funding_base_snapshot : row.current_funding_base, 'USD') }}
                  · Previously funded: {{ formatAmount(row.retained ? row.funded_before_snapshot : row.confirmed_funding_amount, 'USD') }}
                </div>
                <div v-if="!row.retained" class="funding-editor__hint">Available: {{ formatAmount(row.available_funding_amount, row.currency) }}</div>
              </div>
              <app-form-group v-if="partial && !row.bookings" :label="'Funding amount (' + row.currency + ')'" :label-for="modalName + '-amount-' + groupId(row.group)" class="form-group--no-indent" required>
                <app-input :id="modalName + '-amount-' + groupId(row.group)" v-model="row.amount_usd" inputmode="decimal" :disabled="busy" :error="!!amountError(row)" />
                <template v-if="amountError(row)" #error>{{ amountError(row) }}</template>
              </app-form-group>
              <strong v-else>{{ formatAmount(sumFunding([row], 'amount_usd'), row.currency) }}</strong>
              <app-button theme="dark" size="small" :disabled="busy" :aria-label="'Remove ' + groupName(row.group)" @click.native="removeRow(row)">Remove</app-button>
              <funding-bookings v-if="row.bookings" :bookings="row.bookings" :input-prefix="modalName + '-' + groupId(row.group)" :retained="row.retained" :disabled="busy" editable class="funding-editor__bookings" />
            </div>
            <app-cells v-if="coverageError">{{ coverageError }}</app-cells>
            <app-cells v-if="availabilityFailed">Current availability could not be loaded. You can still reduce or remove saved bookings. <app-button :disabled="busy" @click.native="refreshAvailability">Retry</app-button></app-cells>
            <app-cells v-if="!rows.length">No tours selected. Saving an empty draft releases all its reservations.</app-cells>
            <app-cells v-if="batch && canAdd">
              <app-button :disabled="busy" @click.native="showPicker = !showPicker">{{ showPicker ? 'Hide available tours' : 'Add tours' }}</app-button>
            </app-cells>
            <funding-tour-picker v-if="showPicker && canAdd" :relationship="relationship" :recipient="recipient" :excluded="selectedIds" :draft-batch-id="batch ? batch.id : null" :disabled="busy" @add="addRow" />
            <app-cells v-if="removedRows.length">
              <span>Removed from this draft:</span>
              <app-button v-for="row in removedRows" :key="groupId(row.group)" theme="dark" size="small" :disabled="busy" @click.native="restoreRow(row)">Undo removal: {{ groupName(row.group) }}</app-button>
            </app-cells>
            <app-cells position="end"><strong>Total: {{ formatAmount(fundingAmount, currency) }}</strong></app-cells>
            <app-cells position="center" :indent="false">
              <app-button theme="dark" :disabled="busy" @click.native="close">Close</app-button>
              <app-button ref="confirmButton" type="submit" :aria-busy="busy" :disabled="busy || !validSelection">{{ batch ? 'Save draft' : 'Create draft' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getTransferPricingFundingQueue, patchTransferPricingFundingBatch, postTransferPricingFundingBatch, postTransferPricingFundingProposal } from '@/http'
import FundingTourPicker from './TourPicker'
import FundingBookings from './Bookings'
import {
  canSelect, coverageSelectionError, directionParams, editableSelectionValid, formatAmount, formatDate, fundingLines,
  groupId, groupName, isAltezzaBatch, lineAmountError, positiveAmount,
  proposalRows, proposalSelectionValid, relationshipName, retainedRows, selectedRows, sumFunding,
} from './funding'

export default {
  name: 'FundingModal',
  components: { FundingTourPicker, FundingBookings },
  props: {
    recipient: { type: String, default: '' },
    relationship: { type: String, default: '' },
    direction: { type: Object, default: null },
  },
  data() {
    return { availabilityFailed: false, rows: [], removedRows: [], batch: null, selectionBudget: '', submitted: false, busy: false, showPicker: false, generation: 0 }
  },
  beforeDestroy() { this.generation++ },
  computed: {
    modalName() { return `funding-${this.relationship || this.recipient}` },
    proposalName() { return `${this.modalName}-proposal` },
    partial() { return !!this.relationship || (!!this.batch && isAltezzaBatch(this.batch)) },
    historical() { return this.partial && !!this.batch && !this.batch.relationship },
    canAdd() { return !this.historical && (!this.direction || this.direction.active) },
    currency() { return this.rows.length ? this.rows[0].currency : (this.batch ? this.batch.currency : 'USD') },
    fundingAmount() { return sumFunding(this.rows, 'amount_usd') },
    selectedIds() { return this.rows.map(row => groupId(row.group)) },
    budgetInvalid() { return this.submitted && !positiveAmount(this.selectionBudget) },
    coverageError() { return coverageSelectionError(this.rows) },
    validSelection() { return editableSelectionValid(this.rows, this.partial, !!this.batch, this.historical) },
  },
  methods: {
    formatAmount,
    formatDate,
    groupId,
    groupName,
    relationshipName,
    sumFunding,
    amountError(row) { return lineAmountError(row, this.partial, this.historical) },
    reset() {
      this.generation++
      this.availabilityFailed = false
      this.rows = []
      this.removedRows = []
      this.batch = null
      this.showPicker = false
      this.selectionBudget = ''
      this.submitted = false
    },
    openProposal() {
      if (this.busy) return
      this.reset()
      this.$modal.show(this.proposalName)
    },
    openSelection(rows) {
      if (this.busy) return
      this.reset()
      this.rows = selectedRows(rows)
      this.$modal.show(this.modalName)
    },
    openDraft(batch) {
      if (this.busy || batch.status !== 'draft') return
      this.reset()
      this.batch = batch
      this.rows = retainedRows(batch.lines)
      if (this.rows.some(row => row.bookings)) this.refreshAvailability()
      this.$modal.show(this.modalName)
      this.$emit('editing', true)
    },
    async refreshAvailability() {
      const generation = this.generation
      const batch = this.batch
      if (!batch || !batch.relationship) return
      this.availabilityFailed = false
      try {
        const responses = await Promise.all(this.rows.filter(row => row.bookings).map(row => getTransferPricingFundingQueue({ relationship: batch.relationship.slug, draft_batch_id: batch.id, climbing_group_id: groupId(row.group), availability: 'all' })))
        if (generation !== this.generation) return
        responses.forEach(({ data }) => data.results.forEach(current => {
          const row = this.rows.find(item => groupId(item.group) === groupId(current.group))
          if (!row) return
          const old = new Map(row.bookings.map(b => [b.client_group_id, b]))
          row.bookings = current.bookings.map(b => ({ ...b, selected: false, amount_usd: b.available_funding_amount, original_amount: '0.00', ...(old.has(b.client_group_id) ? { selected: old.get(b.client_group_id).selected, amount_usd: old.get(b.client_group_id).amount_usd, original_amount: old.get(b.client_group_id).original_amount } : {}) }))
          old.forEach(b => { if (!row.bookings.some(item => item.client_group_id === b.client_group_id)) row.bookings.push({ ...b, availability: 'unavailable', unavailable_reason: 'membership_changed', available_funding_amount: '0.00' }) })
        }))
      } catch (error) {
        if (generation === this.generation) this.availabilityFailed = true
      }
    },
    beforeClose(event) { if (this.busy) event.cancel() },
    onClosed() {
      this.showPicker = false
      this.$emit('editing', false)
    },
    close() { if (!this.busy) this.$modal.hide(this.modalName) },
    removeRow(row) {
      if (this.busy) return
      if (row.retained) this.removedRows.push(row)
      this.rows = this.rows.filter(item => groupId(item.group) !== groupId(row.group))
    },
    restoreRow(row) {
      if (this.busy || this.selectedIds.includes(groupId(row.group))) return
      this.rows.push(row)
      this.removedRows = this.removedRows.filter(item => item !== row)
    },
    addRow(row) {
      if (this.busy || !this.canAdd || !canSelect(row) || this.selectedIds.includes(groupId(row.group))) return
      if (this.rows.length && this.rows[0].currency !== row.currency) return
      this.rows.push(...selectedRows([row], this.rows))
    },
    async propose() {
      this.submitted = true
      if (this.busy || !positiveAmount(this.selectionBudget)) return
      const generation = this.generation
      const button = this.$refs.proposeButton
      button.preload = true
      this.busy = true
      try {
        const { data } = await postTransferPricingFundingProposal({ ...directionParams(this.relationship, this.recipient), target_amount: this.selectionBudget })
        if (generation !== this.generation) return
        if (!data.results.length) {
          this.$notify({ type: 'info', title: 'No tours available for this amount' })
          return
        }
        const rows = proposalRows(data, this.partial)
        if (!proposalSelectionValid(rows, data.target_amount, this.partial)) {
          this.$notify({ type: 'error', title: 'Unable to use the proposed tours. Please try again.' })
          return
        }
        this.rows = rows
        this.submitted = false
        this.busy = false
        this.$modal.hide(this.proposalName)
        this.$modal.show(this.modalName)
      } catch (error) {
        // API errors are displayed by the shared Axios interceptor.
      } finally {
        button.preload = false
        this.busy = false
      }
    },
    async createBatch() {
      this.submitted = true
      if (this.busy || !this.validSelection) return
      const generation = this.generation
      const button = this.$refs.confirmButton
      button.preload = true
      this.busy = true
      try {
        const payload = { lines: fundingLines(this.rows) }
        const { data } = this.batch ? await patchTransferPricingFundingBatch(this.batch.id, payload) : await postTransferPricingFundingBatch({ ...directionParams(this.relationship, this.recipient), ...payload })
        if (generation !== this.generation) return
        this.busy = false
        this.close()
        this.$notify({ type: 'success', title: this.batch ? 'Funding draft saved' : 'Funding draft created' })
        this.$emit(this.batch ? 'saved' : 'created', data)
      } catch (error) {
        // Preserve the selection and form so the user can retry.
      } finally {
        button.preload = false
        this.busy = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.funding-editor__line
  display: grid
  grid-template-columns: minmax(180px, 1fr) 220px auto
  align-items: center
  gap: 12px
  padding: 14px 0
  border-bottom: 1px solid $color-border

  @media (max-width: 650px)
    grid-template-columns: 1fr

.funding-editor__bookings
  grid-column: 1 / -1

.funding-editor__hint
  margin-top: 5px
  color: $color-text-second
  font-size: 12px
</style>
